import React from 'react';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid
} from 'recharts';
import StatisticsGlassCard from './StatisticsGlassCard';
import { DESIGN_TOKENS } from '../../constants/designTokens';
import StatisticsDesktopSectionHeader from './StatisticsDesktopSectionHeader';
import { STATS_ICONS } from './statisticsAssets';
import useChartContainerReady from './useChartContainerReady';

export default function StatisticsDesktopDemographicsCard({
    demographicsStats,
    isEditMode,
    longPressHandlers,
    theme
}) {
    const { containerRef, isReady } = useChartContainerReady();
    const primaryColor = theme?.primaryHex || DESIGN_TOKENS.brand.primaryHex;

    const genderColors = {
        '남': '#3B82F6',
        '여': '#EC4899',
        '미상': '#D1D5DB'
    };

    const gradeData = demographicsStats?.grades || [];
    const genderData = demographicsStats?.gender || [];
    const totalGender = genderData.reduce((acc, d) => acc + d.value, 0);

    return (
        <StatisticsGlassCard className="h-full p-6 flex flex-col" {...longPressHandlers} isEditMode={isEditMode}>
            <div className="w-full mb-2">
                <StatisticsDesktopSectionHeader
                    iconSrc={STATS_ICONS.people}
                    title="인구 통계"
                    badge="분포"
                />
            </div>

            <div ref={containerRef} className="relative w-full flex-1 flex flex-col min-h-[160px]">
                {isReady ? (
                    <div className="flex-1 flex gap-4 h-full">
                        {/* Gender Pie Chart */}
                        <div className="w-[38%] h-full flex flex-col items-center justify-center relative">
                            <ResponsiveContainer width="100%" height="85%">
                                <PieChart>
                                    <defs>
                                        {genderData.map((entry, i) => (
                                            <linearGradient key={`gg-${i}`} id={`genderGrad-${i}`} x1="0" y1="0" x2="1" y2="1">
                                                <stop offset="0%" stopColor={genderColors[entry.name] || '#D1D5DB'} stopOpacity={1} />
                                                <stop offset="100%" stopColor={genderColors[entry.name] || '#D1D5DB'} stopOpacity={0.65} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <Pie
                                        data={genderData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={28}
                                        outerRadius={44}
                                        paddingAngle={3}
                                        dataKey="value"
                                        animationDuration={1200}
                                        cornerRadius={3}
                                    >
                                        {genderData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={`url(#genderGrad-${index})`} stroke="none" />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'rgba(255,255,255,0.98)',
                                            border: 'none',
                                            borderRadius: '14px',
                                            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
                                            padding: '8px 12px',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                        }}
                                        formatter={(value, name) => [`${value}명`, name]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ top: '-8%' }}>
                                <span className="text-sm font-black text-gray-700 font-do-hyeon">{totalGender}</span>
                            </div>
                            <div className="text-[10px] font-bold text-gray-400 mt-0.5 tracking-wide">성별 비율</div>
                        </div>

                        {/* Grades Bar Chart */}
                        <div className="w-[62%] h-full flex flex-col items-center">
                            <ResponsiveContainer width="100%" height="85%">
                                <BarChart data={gradeData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="gradeBarGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={primaryColor} stopOpacity={0.9} />
                                            <stop offset="100%" stopColor={primaryColor} stopOpacity={0.5} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                    <XAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: '#D1D5DB', fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                                        contentStyle={{
                                            backgroundColor: 'rgba(255,255,255,0.98)',
                                            border: 'none',
                                            borderRadius: '14px',
                                            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
                                            padding: '8px 12px',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                        }}
                                        formatter={(value) => [`${value}명`, '분포']}
                                    />
                                    <Bar dataKey="value" fill="url(#gradeBarGrad)" radius={[6, 6, 0, 0]} barSize={18} animationDuration={1200} />
                                </BarChart>
                            </ResponsiveContainer>
                            <div className="text-[10px] font-bold text-gray-400 mt-0.5 tracking-wide">직분 분포 TOP 5</div>
                        </div>
                    </div>
                ) : null}
            </div>
        </StatisticsGlassCard>
    );
}
